import React, {useMemo, useRef} from 'react';
import {Box, Divider, Stack} from '@mui/material';
import {useSearchParams} from 'react-router';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import previewTheme from './previewTheme';
import ReferencesList from './ReferencesList';
import useReferenceProcessing from './useReferenceProcessing';
import ReportContent from './ReportContent';
import DisclosureContent from './DisclosureContent';
import {ErrorState, LoadingState, NoContentState} from './PreviewStates';
import UserMentionPopover from '../../../components/prosemirror-editor/components/UserMentionPopover';
import HashtagPopover from '../../../components/prosemirror-editor/components/HashtagPopover';
import {useUserMentionPopover} from '../../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {useHashtagPopover} from '../../../components/prosemirror-editor/hooks/useHashtagPopover';
import {useReportStore} from '../useReportStore';
import useApprovedReportData from '../reportPage/useApprovedReportData';
import DeltaPanel from './delta/DeltaPanel';
import {buildExcerptSearchTerms, useReportExcerptScroll} from './useReportExcerptScroll';
import IirEvaluationSection from '../../collection/iirEvaluations/IirEvaluationSection';

/**
 * Dedicated preview page for approved products.
 *
 * This is intentionally separate from ReportPreviewPage so approved-only
 * components can evolve without impacting draft preview flows.
 */
const ApprovedProductPreviewPage = () => {
    const reportId = useReportStore((s) => s.id);
    const productType = useReportStore((s) => s.productType);
    const isDisclosure = productType?.value === 'disclosure';
    const contentRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [searchParams] = useSearchParams();

    const searchTerms = useMemo(
        () => buildExcerptSearchTerms(searchParams.get('q')),
        [searchParams],
    );

    const [selectedAuthorId, setSelectedAuthorId] = React.useState(null);

    const {
        anchorEl: mentionAnchorEl,
        anchorPosition: mentionAnchorPosition,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    const {
        anchorEl: hashtagAnchorEl,
        anchorPosition: hashtagAnchorPosition,
        tagData: hashtagData,
        open: hashtagPopoverOpen,
        handleHashtagClick,
        handleClose: handleHashtagPopoverClose,
    } = useHashtagPopover();

    const {
        version,
        latestVersion,
        reportDoc,
        headerDoc,
        content,
        references,
        meta,
        isLoading,
        error,
    } = useApprovedReportData(reportId);

    const {
        localReferences,
        referenceNumbers,
        referencesByMarkId,
        getReferenceNumber,
    } = useReferenceProcessing(content, references);

    useReportExcerptScroll(contentRef, scrollContainerRef, searchTerms, isLoading, Boolean(content));

    const handleAuthorClick = (authorId) => {
        setSelectedAuthorId((prev) => (prev === authorId ? null : authorId));
    };

    const ContentComponent = isDisclosure ? DisclosureContent : ReportContent;

    const mainPreviewContent = (
        <Box
            ref={scrollContainerRef}
            data-report-scroll-root
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                overflowY: 'auto',
                bgcolor: 'white',
                height: 'calc(100vh - 64px)',
            }}
        >
            <ThemeProvider theme={previewTheme}>
                <CssBaseline/>
                <Stack className="preview-root" spacing={2} sx={{padding: 2}} maxWidth="800px" width="100%" margin="0 auto">
                    {isLoading && <LoadingState/>}
                    {error && !isLoading && <ErrorState error={error}/>}

                    {version && !isLoading && !error && !content && <NoContentState version={version}/>}

                    {!isLoading && !error && content && (
                        <Box ref={contentRef}>
                            <ContentComponent
                                content={content}
                                meta={meta}
                                reportDoc={headerDoc || reportDoc}
                                version={version}
                                latestVersion={latestVersion}
                                referencesByMarkId={referencesByMarkId}
                                referenceNumbers={referenceNumbers}
                                getReferenceNumber={getReferenceNumber}
                                selectedAuthorId={selectedAuthorId}
                                onAuthorClick={handleAuthorClick}
                                onUserMentionClick={handleUserMentionClick}
                                onHashtagClick={handleHashtagClick}
                            />
                        </Box>
                    )}

                    {!isLoading && !error && content && (
                        <>
                            <Divider/>
                            <ReferencesList references={localReferences} getReferenceNumber={getReferenceNumber}/>
                        </>
                    )}

                    {!isLoading && !error && content && (
                        <IirEvaluationSection
                            reportId={reportId}
                            productType={meta?.productType ?? productType}
                            status={
                                (headerDoc || reportDoc)?.status
                                || meta?.workflowStatus
                                || 'approved'
                            }
                            enabled={!isDisclosure}
                        />
                    )}
                </Stack>
            </ThemeProvider>
        </Box>
    );

    return (
        <>
            {isDisclosure ? (
                mainPreviewContent
            ) : (
                <Stack direction="row" sx={{height: 'calc(100vh - 64px)', overflow: 'hidden'}}>
                    {mainPreviewContent}
                    <DeltaPanel
                        content={content}
                        reportDoc={{...(headerDoc || reportDoc), id: reportId}}
                        isProductLoading={isLoading}
                    />
                </Stack>
            )}

            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                anchorPosition={mentionAnchorPosition}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />

            <HashtagPopover
                open={hashtagPopoverOpen}
                anchorEl={hashtagAnchorEl}
                anchorPosition={hashtagAnchorPosition}
                onClose={handleHashtagPopoverClose}
                tagData={hashtagData}
            />
        </>
    );
};

export default ApprovedProductPreviewPage;

